import React from 'react';
import {
    Card,
    Table,
    Tabs,
    Input,
    Select,
    Button,
    Space,
    message,
    Spin,
    Drawer,
    Form,
    Popconfirm,
} from 'antd';
import { ReloadOutlined, SearchOutlined, PlusOutlined } from '@ant-design/icons';
import {
    listNewsCategories,
    listNews,
    getNewsFeed,
    getNewsDetail,
    listNewsSubscriptions,
    addNewsSubscription,
    removeNewsSubscription,
} from '../../api/news';
import { formatDateTime } from '../../utils/format';
import { isSafeHttpUrl as checkSafeHttpUrl } from '../../utils/urlSafety';
import { drawerWidth, isMobileViewport } from '../../utils/responsive';
import './NewsBrowser.css';

const CATEGORY_FALLBACK = [
    { code: 'MACRO', name: '宏观' },
    { code: 'WEALTH', name: '理财' },
    { code: 'FINANCE', name: '财经' },
    { code: 'FUND', name: '基金' },
    { code: 'STOCK', name: '股票' },
    { code: 'GLOBAL', name: '全球' },
];

class NewsBrowser extends React.Component {
    constructor(props) {
        super(props);
        this.subFormRef = React.createRef();
        this.state = {
            activeTab: 'feed',
            loading: false,
            categories: [],
            keyword: '',
            category: '',
            page: 1,
            pageSize: 20,
            total: 0,
            items: [],
            subscriptions: [],
            detailOpen: false,
            detail: null,
            subOpen: false,
            subSaving: false,
        };
    }

    componentDidMount() {
        this.loadCategories();
        this.loadFeed();
        this.loadSubscriptions();
    }

    loadCategories = async () => {
        try {
            const raw = await listNewsCategories();
            const categories = (raw || []).map((c) =>
                typeof c === 'string'
                    ? { code: c, name: c }
                    : { code: c.code || c.categoryCode, name: c.name || c.categoryName || c.code }
            );
            this.setState({ categories: categories.length ? categories : CATEGORY_FALLBACK });
        } catch {
            this.setState({ categories: CATEGORY_FALLBACK });
        }
    };

    loadFeed = async () => {
        const { page, pageSize } = this.state;
        this.setState({ loading: true });
        try {
            const data = await getNewsFeed({ page, size: pageSize });
            this.setState({
                items: data?.items || [],
                total: data?.total || 0,
            });
        } catch (e) {
            message.error(e.message || '加载 Feed 失败');
        } finally {
            this.setState({ loading: false });
        }
    };

    loadList = async () => {
        const { page, pageSize, category, keyword } = this.state;
        this.setState({ loading: true });
        try {
            const data = await listNews({ category, keyword, page, size: pageSize });
            this.setState({
                items: data?.items || [],
                total: data?.total || 0,
            });
        } catch (e) {
            message.error(e.message || '加载资讯失败');
        } finally {
            this.setState({ loading: false });
        }
    };

    loadSubscriptions = async () => {
        try {
            const subscriptions = await listNewsSubscriptions();
            this.setState({ subscriptions });
        } catch (e) {
            message.error(e.message || '加载订阅失败');
        }
    };

    loadCurrent = () => {
        if (this.state.activeTab === 'feed') {
            this.loadFeed();
        } else if (this.state.activeTab === 'list') {
            this.loadList();
        }
    };

    handleTabChange = (activeTab) => {
        this.setState({ activeTab, page: 1 }, () => {
            if (activeTab === 'feed') {
                this.loadFeed();
            } else if (activeTab === 'list') {
                this.loadList();
            }
        });
    };

    openDetail = async (id) => {
        try {
            const detail = await getNewsDetail(id);
            this.setState({ detailOpen: true, detail });
        } catch (e) {
            message.error(e.message || '加载详情失败');
        }
    };

    handleAddSubscription = async () => {
        const form = this.subFormRef.current;
        if (!form) {
            return;
        }
        let values;
        try {
            values = await form.validateFields();
        } catch {
            return;
        }
        if (!values.categoryCode && !values.keyword && !values.entityCode) {
            message.warning('分类、关键字、标的代码至少填一项');
            return;
        }
        this.setState({ subSaving: true });
        try {
            await addNewsSubscription(values);
            message.success('订阅已添加');
            this.setState({ subOpen: false });
            form.resetFields();
            await this.loadSubscriptions();
            if (this.state.activeTab === 'feed') {
                await this.loadFeed();
            }
        } catch (e) {
            message.error(e.message || '订阅失败');
        } finally {
            this.setState({ subSaving: false });
        }
    };

    handleRemoveSubscription = async (id) => {
        try {
            await removeNewsSubscription(id);
            message.success('已取消订阅');
            await this.loadSubscriptions();
            if (this.state.activeTab === 'feed') {
                await this.loadFeed();
            }
        } catch (e) {
            message.error(e.message || '取消失败');
        }
    };

    renderArticleTable = () => {
        const { items, loading, page, pageSize, total } = this.state;
        return (
            <Spin spinning={loading}>
                <Table
                    size="small"
                    rowKey="id"
                    scroll={{ x: 'max-content' }}
                    dataSource={items}
                    pagination={{
                        current: page,
                        pageSize,
                        total,
                        showSizeChanger: true,
                        onChange: (p, ps) =>
                            this.setState({ page: p, pageSize: ps }, this.loadCurrent),
                    }}
                    columns={[
                        {
                            title: '标题',
                            dataIndex: 'title',
                            ellipsis: true,
                            render: (t, row) => (
                                <Button type="link" size="small" onClick={() => this.openDetail(row.id)}>
                                    {t}
                                </Button>
                            ),
                        },
                        { title: '分类', dataIndex: 'categoryName', width: 90 },
                        { title: '来源', dataIndex: 'sourceName', width: 100, ellipsis: true },
                        {
                            title: '发布时间',
                            dataIndex: 'publishTime',
                            width: 160,
                            render: formatDateTime,
                        },
                    ]}
                />
            </Spin>
        );
    };

    render() {
        const {
            activeTab,
            categories,
            keyword,
            category,
            subscriptions,
            detailOpen,
            detail,
            subOpen,
            subSaving,
        } = this.state;

        const categoryOptions = [
            { value: '', label: '全部分类' },
            ...categories.map((c) => ({ value: c.code, label: c.name || c.code })),
        ];

        const tabItems = [
            {
                key: 'feed',
                label: '个性化 Feed',
                children: (
                    <>
                        <Space className="news-toolbar">
                            <Button icon={<ReloadOutlined />} onClick={this.loadFeed}>
                                刷新
                            </Button>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => this.setState({ subOpen: true })}
                            >
                                添加订阅
                            </Button>
                        </Space>
                        {this.renderArticleTable()}
                    </>
                ),
            },
            {
                key: 'list',
                label: '资讯库',
                children: (
                    <>
                        <Space wrap className="news-toolbar">
                            <Input
                                placeholder="关键字"
                                value={keyword}
                                onChange={(e) => this.setState({ keyword: e.target.value })}
                                onPressEnter={this.loadList}
                                style={{ width: 180 }}
                            />
                            <Select
                                value={category}
                                options={categoryOptions}
                                style={{ width: 120 }}
                                onChange={(v) => this.setState({ category: v })}
                            />
                            <Button type="primary" icon={<SearchOutlined />} onClick={this.loadList}>
                                查询
                            </Button>
                        </Space>
                        {this.renderArticleTable()}
                    </>
                ),
            },
            {
                key: 'subscriptions',
                label: '我的订阅',
                children: (
                    <Table
                        size="small"
                        rowKey="id"
                        scroll={{ x: 'max-content' }}
                        dataSource={subscriptions}
                        pagination={false}
                        columns={[
                            { title: '分类', dataIndex: 'categoryCode' },
                            { title: '关键字', dataIndex: 'keyword' },
                            {
                                title: '标的',
                                render: (_, r) =>
                                    r.entityType && r.entityCode
                                        ? `${r.entityType}:${r.entityCode}`
                                        : '—',
                            },
                            {
                                title: '操作',
                                render: (_, row) => (
                                    <Popconfirm
                                        title="取消该订阅？"
                                        onConfirm={() => this.handleRemoveSubscription(row.id)}
                                    >
                                        <Button type="link" size="small" danger>
                                            取消
                                        </Button>
                                    </Popconfirm>
                                ),
                            },
                        ]}
                    />
                ),
            },
        ];

        const mobile = isMobileViewport();

        return (
            <div className="news-browser">
                <Card title="财经资讯" bordered={false}>
                    <Tabs activeKey={activeTab} items={tabItems} onChange={this.handleTabChange} />
                </Card>

                <Drawer
                    title={detail?.title || '资讯详情'}
                    open={detailOpen}
                    placement={mobile ? 'bottom' : 'right'}
                    height={mobile ? '80%' : undefined}
                    width={drawerWidth(520)}
                    onClose={() => this.setState({ detailOpen: false, detail: null })}
                >
                    {detail && (
                        <div className="news-detail-drawer">
                            <p>
                                <strong>分类：</strong>
                                {detail.categoryName || detail.categoryCode}
                            </p>
                            <p>
                                <strong>时间：</strong>
                                {formatDateTime(detail.publishTime)}
                            </p>
                            <p>
                                <strong>摘要：</strong>
                                {detail.summary || '—'}
                            </p>
                            {checkSafeHttpUrl(detail.sourceUrl) && (
                                <p>
                                    <a
                                        href={detail.sourceUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        阅读原文
                                    </a>
                                </p>
                            )}
                            {detail.entityLinks?.length > 0 && (
                                <p>
                                    <strong>关联：</strong>
                                    {detail.entityLinks
                                        .map((l) => `${l.entityType}:${l.entityCode}`)
                                        .join('; ')}
                                </p>
                            )}
                        </div>
                    )}
                </Drawer>

                <Drawer
                    title="添加订阅"
                    open={subOpen}
                    placement={mobile ? 'bottom' : 'right'}
                    height={mobile ? '75%' : undefined}
                    width={drawerWidth(400)}
                    onClose={() => this.setState({ subOpen: false })}
                    extra={
                        <Button type="primary" loading={subSaving} onClick={this.handleAddSubscription}>
                            保存
                        </Button>
                    }
                >
                    <Form ref={this.subFormRef} layout="vertical">
                        <Form.Item name="categoryCode" label="分类代码">
                            <Select
                                allowClear
                                options={categories.map((c) => ({
                                    value: c.code,
                                    label: `${c.name || c.code} (${c.code})`,
                                }))}
                            />
                        </Form.Item>
                        <Form.Item name="keyword" label="关键字">
                            <Input placeholder="标题/摘要包含" />
                        </Form.Item>
                        <Form.Item name="entityType" label="标的类型">
                            <Select
                                allowClear
                                options={[
                                    { value: 'FUND', label: '基金 FUND' },
                                    { value: 'STOCK', label: '股票 STOCK' },
                                ]}
                            />
                        </Form.Item>
                        <Form.Item name="entityCode" label="标的代码">
                            <Input placeholder="如 110011" />
                        </Form.Item>
                    </Form>
                </Drawer>
            </div>
        );
    }
}

export default NewsBrowser;
